import { isAxiosError } from "axios";
import axiosInstance from "@/axiosConfig";
import type { JobServiceResponse } from "@/shared/types/job.types";
import { getApiUrl } from "@/shared/utils/api";
import { buildSearchParams } from "@/shared/utils/query-params";
import { ACCOUNTING_ENDPOINTS } from "../configs/endpoints";
import {
  fromApiBankMovement,
  fromApiCategorizationRule,
  fromApiDuplicateCandidate,
  fromApiLedgerAllocation,
  fromApiSepaRemittance,
  fromApiSettlement,
  toApiLedgerAllocationPayload,
} from "../core/mappers/admin/accounting.mapper";
import { applyNetBudgetSynthesis } from "../utils/netBudgetSynthesis";
import type {
  BankMovementAccountsListResponse,
  BankMovementFusionResponse,
  BankMovementProbableMatchingRunResponse,
  BankMovementResponse,
  BankMovementPaymentModesListResponse,
  ComptaMouvementsListResponse,
  ComptaMouvementAffectationsListResponse,
  ComptaMouvementAffectationResponse,
  ComptaMouvementApiResource,
  ComptaMouvementAffectationApiResource,
  LedgerAllocationCategorizationRuleResponse,
  LedgerAllocationCategorizationRulesListResponse,
  LedgerAllocationOperationTypeResponse,
  LedgerAllocationOperationTypesListResponse,
  SettlementsListResponse,
  SettlementResponse,
  SettlementPaymentMethodsListResponse,
  StockCategoriesListResponse,
  StockFamiliesListResponse,
  StockReferencesListResponse,
  SepaRemittanceApiResource,
  SepaRemittancesListResponse,
} from "../types/accounting-api.types";
import type {
  SepaRemittanceEventInput,
  SepaBankTreatmentInput,
  SepaBankTreatmentPreview,
  BankMovementAccount,
  BankMovementAccountsQueryParams,
  BankMovementFusionCandidatesQueryParams,
  BankMovementPaymentModesQueryParams,
  BankMovement,
  BankMovementsListResult,
  BankMovementsQueryParams,
  BankMovementWritePayload,
  LedgerAllocationsListResult,
  LedgerAllocationsQueryParams,
  LedgerAllocationOperationType,
  LedgerOperationTypeFacets,
  LedgerAllocationCategorizationRule,
  LedgerAllocationCategorizationRuleCreatePayload,
  LedgerAllocationCategorizationRulesQueryParams,
  LedgerAllocationCategorizationRuleUpdatePayload,
  LedgerAllocationOperationTypesQueryParams,
  LedgerAllocation,
  LedgerAllocationWritePayload,
  StockFamily,
  StockReference,
  RefundEmployeeSearchResult,
  RefundSearchResult,
  EcommerceOrderSearchResult,
  Settlement,
  SettlementsListResult,
  SettlementsQueryParams,
  SettlementWritePayload,
  CreateRemisePayload,
  CreateRemiseResponse,
  RemisePdfResponse,
  SepaExportPayload,
  SepaExportResponse,
  SepaRemittance,
  SepaRemittanceFile,
  SepaRemittanceRegenerateResult,
  SepaRemittanceRollbackResult,
  SepaRemittancesListResult,
  SepaRemittancesQueryParams,
  SepaValidationErrorDetail,
  SepaEligibility,
  SepaValidateResult,
  SynthesisQueryParams,
  SynthesisResult,
  SynthesisDetailQueryParams,
  SynthesisDetailResult,
  BankAccountSettings,
  BankAccountWritePayload,
  BridgeAccount,
  BridgeProvider,
  CseBudgetType,
  AccountingBudget,
  AccountingBudgetsQueryParams,
  AccountingBudgetsResult,
  AccountingBudgetUpsertPayload,
  AccountingSettings,
  AccountingSettingsUpdatePayload,
  BankMovementDuplicateCandidate,
} from "../types/accounting.types";
import type { NetBudgetSynthesisApi } from "../utils/netBudgetSynthesis";

const DEFAULT_LIMIT = 20;

// ADM-621 : payload brut des paramètres comptables, partagé par le GET et le PATCH.
type AccountingSettingsApiPayload = {
  closing_day: number
  closing_month: number
  calendar: boolean
  configured: boolean
  pcg_accounts_count?: number
  current_exercice: number
  current_exercice_start: string
  current_exercice_end: string
  selectable_exercices: Array<{
    exercice: number
    label: string
    start_date: string
    end_date: string
  }>
};

// ADM-621 : payload brut d'une ligne de budget, globale ou par catégorie.
type AccountingBudgetApiPayload = {
  nature?: "expense" | "revenue"
  id: number
  exercice: number
  montant: number
  commentaire: string | null
  demo?: boolean
  operation_type_id: number | null
  operation_type_name: string | null
  type_budget: CseBudgetType | null
};

const mapBudget = (payload: AccountingBudgetApiPayload): AccountingBudget => ({
  id: payload.id,
  exercice: payload.exercice,
  montant: payload.montant,
  commentaire: payload.commentaire,
  demo: payload.demo ?? false,
  operationTypeId: payload.operation_type_id,
  operationTypeName: payload.operation_type_name,
  budgetType: payload.type_budget,
  nature: payload.nature,
});

const mapAccountingSettings = (payload: AccountingSettingsApiPayload): AccountingSettings => ({
  closingDay: payload.closing_day,
  closingMonth: payload.closing_month,
  calendar: payload.calendar,
  configured: payload.configured,
  pcgAccountsCount: payload.pcg_accounts_count ?? 0,
  currentExercice: payload.current_exercice,
  currentExerciceStart: payload.current_exercice_start,
  currentExerciceEnd: payload.current_exercice_end,
  selectableExercices: (payload.selectable_exercices ?? []).map((option) => ({
    exercice: option.exercice,
    label: option.label,
    startDate: option.start_date,
    endDate: option.end_date,
  })),
});

export type SepaValidationError = { details: SepaValidationErrorDetail[] } & Error;

export type BridgeTransferValidationError = {
  salarie: string
  champ: string
  message: string
};

export type BridgeTransferResponse = {
  consent_url?: string
  error?: string
  validation_errors?: BridgeTransferValidationError[]
};

export type BridgePaymentStatusResponse = {
  bridge_status: string | null
  /** true = l'utilisateur est allé au bout du consentement chez Bridge. */
  consented: boolean
  releasable: boolean
  /**
   * Lien de consentement, reconstruit par le back depuis l'identifiant du
   * payment request : l'API de Bridge ne le rend pas à la relecture. Nul dès
   * que le lien a plus de quinze minutes, auquel cas la reprise n'est plus
   * possible et il faut libérer l'initiation.
   */
  consent_url?: string | null
  /** Échéance du lien, renseignée même quand `consent_url` est déjà nul. */
  consent_expires_at?: string | null
};

export type BridgeReleaseResponse = {
  released: boolean
  discarded: boolean
  bank_movement_id?: string
  resynced_operations?: number
};

type JsonApiResource<T> = {
  id: string
  attributes?: T
  relationships?: Record<string, { data?: { id: string } | null }>
};

type JsonApiListResponse<T> = {
  data?: JsonApiResource<T>[]
  meta?: {
    current_page?: number
    total_pages?: number
    total_count?: number
    per_page?: number
  }
  included?: Array<{ id: string, type: string, attributes?: Record<string, unknown> }>
};

type JsonApiSingleResponse<T> = { data?: JsonApiResource<T> };

const toMetadata = (meta: JsonApiListResponse<unknown>["meta"] = {}) => ({
  current_page: meta.current_page ?? 1,
  total_pages: meta.total_pages ?? 1,
  total_count: meta.total_count ?? 0,
  total_items: meta.total_count ?? 0,
  per_page: meta.per_page ?? DEFAULT_LIMIT,
});

const mapJsonApiBankMovements = (payload: JsonApiListResponse<ComptaMouvementApiResource>) => {
  const includedAccounts = new Map<string, string>();

  (payload.included ?? []).forEach((resource) => {
    if (resource.type === "compta_entreprise_comptes") {
      const name = resource.attributes?.nom;
      if (typeof name === "string") {
        includedAccounts.set(resource.id, name);
      }
    }
  });

  const resources = payload.data ?? [];

  return resources.map((resource) => {
    const attributes = resource.attributes ?? {};
    const accountId = resource.relationships?.compta_entreprise_compte?.data?.id;
    const accountName = accountId ? includedAccounts.get(accountId) ?? null : null;

    return fromApiBankMovement({
      ...attributes,
      id: resource.id,
      compta_entreprise_compte: accountName ? { nom: accountName } : undefined,
    });
  });
};

const mapJsonApiLedgerAllocations = (
  payload: JsonApiListResponse<ComptaMouvementAffectationApiResource>,
) => {
  const resources = payload.data ?? [];

  return resources.map((resource) => {
    const attributes = resource.attributes ?? {};
    return fromApiLedgerAllocation({
      ...attributes,
      id: resource.id,
    });
  });
};

const mapJsonApiLedgerAllocation = (payload: JsonApiSingleResponse<ComptaMouvementAffectationApiResource>) => {
  const resource = payload.data;

  if (!resource) {
    throw new Error("Réponse JSONAPI invalide: data manquant");
  }

  return fromApiLedgerAllocation({
    ...(resource.attributes ?? {}),
    id: resource.id,
  });
};

const ledgerAllocationsSearchQuery = (params: LedgerAllocationsQueryParams) => ({
  s: params.sorts,
  deep_smart_query: params.label?.trim() || undefined,
  date_eq: params.dateEq,
  date_gteq: params.dateGteq,
  date_lteq: params.dateLteq,
  type_budget_eq: params.budgetTypeEq,
  type_movement_eq: params.movementTypeEq,
  debit_eq: params.debitEq,
  debit_gt: params.debitGt,
  debit_lt: params.debitLt,
  debit_not_eq: params.debitNotEq,
  debit_gteq: params.debitGteq,
  debit_lteq: params.debitLteq,
  credit_eq: params.creditEq,
  credit_gt: params.creditGt,
  credit_lt: params.creditLt,
  credit_not_eq: params.creditNotEq,
  credit_gteq: params.creditGteq,
  credit_lteq: params.creditLteq,
  // Tri-state: true → stock only (Stock tab), false → exclude stock
  // (Grand Livre), undefined → no constraint.
  stock: params.stockOnly,
  resolved_bank_account_id_eq: params.resolvedBankAccountIdEq,
  resolved_bank_account_id_in: params.resolvedBankAccountIds,
  // Ids joints par des virgules : un paramètre par id dépassait la
  // limite de 10 Ko de Puma sur la query string.
  operation_type_id_in: params.operationTypeIds?.join(","),
  operation_type_id_not_in: params.operationTypeExcludedIds?.join(","),
  has_attachments_eq: params.hasAttachmentsEq,
  source_module_cont: params.sourceModuleEq,
  activite_achats_famille_id_in: params.activitePurchaseFamilyIds,
  activite_achats_reference_id_in: params.activitePurchaseReferenceIds,
  tag_ids: params.tagIds?.length ? params.tagIds.join(",") : undefined,
  tag_mode: params.tagMode && params.tagMode !== "any" ? params.tagMode : undefined,
});

export const accountingService = {
  listBankMovementAccounts: async (
    params: BankMovementAccountsQueryParams = {},
  ): Promise<BankMovementAccount[]> => {
    const searchParams = buildSearchParams(
      {
        q: {
          year_eq: params.year,
          smart_query: params.search?.trim(),
          date_eq: params.dateEq,
          date_gteq: params.dateGteq,
          date_lteq: params.dateLteq,
          date_null: params.dateNull ? true : undefined,
          bank_account_id_in: params.bankAccountIds,
        },
      },
    );
    if (params.context) searchParams.append("context", params.context);

    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.LIST_BANK_MOVEMENT_ACCOUNTS}${queryString ? `?${queryString}` : ""}`,
    );

    const response = await axiosInstance.get<BankMovementAccountsListResponse>(url);

    return (response.data.bank_accounts ?? []).map((account) => ({
      id: String(account.id),
      name: account.name,
      budgetType: account.budget_type ?? null,
      accountType: (account.account_type as BankMovementAccount["accountType"]) ?? null,
    }));
  },

  listBankMovementPaymentModes: async (
    params: BankMovementPaymentModesQueryParams = {},
  ): Promise<string[]> => {
    const searchParams = buildSearchParams({
      q: {
        year_eq: params.year,
        deep_smart_query: params.label?.trim(),
        date_eq: params.dateEq,
        date_gteq: params.dateGteq,
        date_lteq: params.dateLteq,
        date_null: params.dateNull ? true : undefined,
        debit_eq: params.debitEq,
        debit_gt: params.debitGt,
        debit_lt: params.debitLt,
        debit_not_eq: params.debitNotEq,
        debit_gteq: params.debitGteq,
        debit_lteq: params.debitLteq,
        credit_eq: params.creditEq,
        credit_gt: params.creditGt,
        credit_lt: params.creditLt,
        credit_not_eq: params.creditNotEq,
        credit_gteq: params.creditGteq,
        credit_lteq: params.creditLteq,
        bank_account_id_in: params.bankAccountIds,
        source_state_in: params.sourceStates,
      },
    });
    if (params.context) searchParams.append("context", params.context);

    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.LIST_BANK_MOVEMENT_PAYMENT_MODES}${queryString ? `?${queryString}` : ""}`,
    );

    const response = await axiosInstance.get<BankMovementPaymentModesListResponse>(url);

    return response.data.payment_modes ?? [];
  },

  getBankMovement: async (bankMovementId: string): Promise<BankMovement | null> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.SHOW_BANK_MOVEMENT(bankMovementId));
    const response = await axiosInstance.get<BankMovementResponse>(url);
    if (!response.data.bank_movement) return null;
    return fromApiBankMovement(response.data.bank_movement);
  },

  // ADM-681 : rejoue le calcul du solde d'affectation côté serveur et renvoie
  // le mouvement à jour. Utilisé à l'ouverture du drawer d'un mouvement en
  // écart pour resynchroniser le solde stocké avec les affectations réelles.
  recalculateBankMovementBalance: async (bankMovementId: string): Promise<BankMovement | null> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.RECALCULATE_BANK_MOVEMENT_BALANCE(bankMovementId));
    const response = await axiosInstance.post<BankMovementResponse>(url);
    if (!response.data.bank_movement) return null;
    return fromApiBankMovement(response.data.bank_movement);
  },

  listBankMovements: async (params: BankMovementsQueryParams = {}): Promise<BankMovementsListResult> => {
    const searchParams = buildSearchParams(
      {
        page: params.page,
        limit: params.limit,
        q: {
          s: params.sorts,
          year_eq: params.year,
          deep_smart_query: params.allocationSearch?.trim() || undefined,
          date_eq: params.dateEq,
          date_gteq: params.dateGteq,
          date_lteq: params.dateLteq,
          date_null: params.dateNull ? true : undefined,
          deposit_date_null: params.depositDateNull ? true : undefined,
          deposit_date_gteq: params.depositDateGteq || undefined,
          deposit_date_lteq: params.depositDateLteq || undefined,
          debit_eq: params.debitEq,
          debit_gt: params.debitGt,
          debit_lt: params.debitLt,
          debit_not_eq: params.debitNotEq,
          debit_gteq: params.debitGteq,
          debit_lteq: params.debitLteq,
          credit_eq: params.creditEq,
          credit_gt: params.creditGt,
          credit_lt: params.creditLt,
          credit_not_eq: params.creditNotEq,
          credit_gteq: params.creditGteq,
          credit_lteq: params.creditLteq,
          payment_method_in: params.paymentModes,
          label_cont: params.labelCont,
          payment_number_cont: params.paymentNumberCont,
          payment_number_null: typeof params.paymentNumberNull === "boolean" ? params.paymentNumberNull : undefined,
          remittance_number_cont: params.remittanceNumberCont,
          remittance_number_null: typeof params.remittanceNumberNull === "boolean" ? params.remittanceNumberNull : undefined,
          probable_fusion_status_eq: params.probableFusionStatusEq,
          allocation_status_eq: params.allocationStatusEq,
          allocation_discrepancy_eq: params.allocationDiscrepancyEq,
          bank_discrepancy_eq: params.bankDiscrepancyEq,
          has_compta_piece_eq: typeof params.hasComptaPieceEq === "boolean" ? params.hasComptaPieceEq : undefined,
          allocation_date_gteq: params.allocationDateGteq || undefined,
          allocation_date_lteq: params.allocationDateLteq || undefined,
          bank_account_id_in: params.bankAccountIds,
          sepa_eligible_for: params.sepaEligibleFor,
          source_state_in: params.sourceStates,
          tag_ids: params.tagIds?.length ? params.tagIds.join(",") : undefined,
          tag_mode: params.tagMode && params.tagMode !== "any" ? params.tagMode : undefined,
        },
      },
      { defaults: { page: 1, limit: DEFAULT_LIMIT } },
    );
    if (params.context) searchParams.append("context", params.context);

    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.LIST_BANK_MOVEMENTS}${queryString ? `?${queryString}` : ""}`,
    );

    const response = await axiosInstance.get<ComptaMouvementsListResponse | JsonApiListResponse<ComptaMouvementApiResource>>(url);

    if ("bank_movements" in response.data || "compta_mouvements" in response.data) {
      return {
        bankMovements: (response.data.bank_movements ?? response.data.compta_mouvements ?? []).map(fromApiBankMovement),
        metadata: response.data.metadata,
      };
    }

    const jsonApiData = response.data as JsonApiListResponse<ComptaMouvementApiResource>;
    return {
      bankMovements: mapJsonApiBankMovements(jsonApiData),
      metadata: toMetadata(jsonApiData.meta),
    };
  },

  listBankMovementFusionCandidates: async (
    params: BankMovementFusionCandidatesQueryParams,
  ): Promise<BankMovement[]> => {
    const searchParams = buildSearchParams({ q: { smart_query: params.smartQuery?.trim() } });

    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.LIST_BANK_MOVEMENT_FUSION_CANDIDATES(params.bankMovementId)}${queryString ? `?${queryString}` : ""}`,
    );

    const response = await axiosInstance.get<ComptaMouvementsListResponse>(url);
    return (response.data.bank_movements ?? response.data.compta_mouvements ?? []).map(fromApiBankMovement);
  },

  fuseBankMovement: async (
    bankMovementId: string,
    candidateId: string,
  ): Promise<BankMovement | null> => {
    const response = await axiosInstance.post<BankMovementFusionResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.FUSE_BANK_MOVEMENT(bankMovementId)),
      { candidate_id: candidateId },
    );

    if (!response.data.bank_movement) return null;
    return fromApiBankMovement(response.data.bank_movement);
  },

  mergeBankMovements: async (params: {
    sourceIds: string[]
    attributes: {
      date?: string | null
      label?: string | null
      bank_account_id?: string | number
      payment_method?: string | null
      payment_number?: string | null
      remittance_number?: string | null
      deposit_date?: string | null
      issue_date?: string | null
      debit?: number | null
      credit?: number | null
      compta_piece?: string | null
    }
  }): Promise<BankMovement | null> => {
    const response = await axiosInstance.post<BankMovementFusionResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.MERGE_BANK_MOVEMENTS),
      {
        source_ids: params.sourceIds,
        attributes: params.attributes,
      },
    );
    if (!response.data.bank_movement) return null;
    return fromApiBankMovement(response.data.bank_movement);
  },

  splitBankMovement: async (
    bankMovementId: string,
  ): Promise<BankMovement | null> => {
    const response = await axiosInstance.post<BankMovementFusionResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.SPLIT_BANK_MOVEMENT(bankMovementId)),
    );

    if (!response.data.bank_movement) return null;
    return fromApiBankMovement(response.data.bank_movement);
  },

  runBankMovementsProbableMatching: async (): Promise<{ runId: string, total: number }> => {
    const response = await axiosInstance.post<BankMovementProbableMatchingRunResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.RUN_BANK_MOVEMENTS_PROBABLE_MATCHING),
    );

    return {
      runId: response.data.run_id ?? "",
      total: response.data.total ?? 0,
    };
  },

  pollProbableMatchingStatus: async (runId: string): Promise<{
    run_id?: string
    status?: string
    total?: number
    processed?: number
    matched?: number
    percent?: number
    error?: string
  } | null> => {
    const response = await axiosInstance.get(
      getApiUrl(ACCOUNTING_ENDPOINTS.PROBABLE_MATCHING_STATUS(runId)),
    );
    return response.data ?? null;
  },

  validateBankMovementProbableFusion: async (
    bankMovementId: string,
  ): Promise<BankMovement | null> => {
    const response = await axiosInstance.post<BankMovementResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.VALIDATE_BANK_MOVEMENT_PROBABLE_FUSION(bankMovementId)),
    );

    if (!response.data.bank_movement) return null;
    return fromApiBankMovement(response.data.bank_movement);
  },

  declineBankMovementProbableFusion: async (
    bankMovementId: string,
  ): Promise<BankMovement | null> => {
    const response = await axiosInstance.post<BankMovementResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.DECLINE_BANK_MOVEMENT_PROBABLE_FUSION(bankMovementId)),
    );

    if (!response.data.bank_movement) return null;
    return fromApiBankMovement(response.data.bank_movement);
  },

  /**
   * DEC-15 : l'élu reconnaît que la banque a raison. La saisie prend le montant
   * du relevé. Le mouvement ressort avec un reste à affecter égal à l'écart
   * quand il était entièrement affecté — le cas de figure quasi général.
   */
  acceptBankMovementBankAmount: async (
    bankMovementId: string,
  ): Promise<BankMovement | null> => {
    const response = await axiosInstance.post<BankMovementResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.ACCEPT_BANK_MOVEMENT_BANK_AMOUNT(bankMovementId)),
    );

    if (!response.data.bank_movement) return null;
    return fromApiBankMovement(response.data.bank_movement);
  },

  /**
   * DEC-15 : l'élu maintient sa saisie contre le relevé. Aucun montant ne bouge,
   * la ligne quitte la file — et y revient si la banque révise son montant.
   */
  keepBankMovementOwnAmount: async (
    bankMovementId: string,
    expected?: { bankCredit: number | null, bankDebit: number | null },
  ): Promise<BankMovement | null> => {
    const response = await axiosInstance.post<BankMovementResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.KEEP_BANK_MOVEMENT_OWN_AMOUNT(bankMovementId)),
      expected
        ? {
          expected_bank_credit: expected.bankCredit ?? 0,
          expected_bank_debit: expected.bankDebit ?? 0,
        }
        : undefined,
    );

    if (!response.data.bank_movement) return null;
    return fromApiBankMovement(response.data.bank_movement);
  },

  /**
   * Le compteur agrégé de DEC-15 : « N écarts en attente d'arbitrage ». Il porte
   * sur toute l'organisation et non sur la vue filtrée, pour que la pastille ne
   * varie pas avec les filtres de l'écran.
   */
  // Le même périmètre de comptes que la liste (`context=ledger` : Bancaire et
  // Caisse) : un écart compté doit se retrouver au clic sur le bandeau.
  getBankMovementsDiscrepancySummary: async (): Promise<{
    pendingCount: number
    pendingAmount: number
    acknowledgedCount: number
  }> => {
    const response = await axiosInstance.get<{
      pending_count?: number | string | null
      pending_amount?: number | string | null
      acknowledged_count?: number | string | null
    }>(getApiUrl(`${ACCOUNTING_ENDPOINTS.BANK_MOVEMENTS_DISCREPANCY_SUMMARY}?context=ledger`));

    return {
      pendingCount: Number(response.data?.pending_count ?? 0),
      pendingAmount: Number(response.data?.pending_amount ?? 0),
      acknowledgedCount: Number(response.data?.acknowledged_count ?? 0),
    };
  },

  /**
   * DEC-15, second volet : les doublons que la tâche de nettoyage écarte, avec
   * leur original. La commande ne les tranchera jamais — leur passage n'atteint
   * pas les seuils de resynchronisation — et aucun prédicat ne peut le faire.
   */
  listBankMovementDuplicateCandidates: async (): Promise<BankMovementDuplicateCandidate[]> => {
    const response = await axiosInstance.get<{ duplicate_candidates?: unknown[] }>(
      getApiUrl(ACCOUNTING_ENDPOINTS.BANK_MOVEMENT_DUPLICATE_CANDIDATES),
    );

    return (response.data?.duplicate_candidates ?? []).map(
      (raw) => fromApiDuplicateCandidate(raw as Parameters<typeof fromApiDuplicateCandidate>[0]),
    );
  },

  /** Le travail suit l'original ; le doublon disparaît. La seule issue qui ne perd rien. */
  reattachBankMovementDuplicate: async (bankMovementId: string): Promise<void> => {
    await axiosInstance.post(getApiUrl(ACCOUNTING_ENDPOINTS.REATTACH_BANK_MOVEMENT_DUPLICATE(bankMovementId)));
  },

  deleteBankMovementDuplicate: async (bankMovementId: string): Promise<void> => {
    await axiosInstance.post(getApiUrl(ACCOUNTING_ENDPOINTS.DELETE_BANK_MOVEMENT_DUPLICATE(bankMovementId)));
  },

  /** Ce n'en est pas un. Noté, parce que rien dans la donnée ne le dira. */
  dismissBankMovementDuplicate: async (bankMovementId: string): Promise<void> => {
    await axiosInstance.post(getApiUrl(ACCOUNTING_ENDPOINTS.DISMISS_BANK_MOVEMENT_DUPLICATE(bankMovementId)));
  },

  updateBankMovement: async (
    bankMovementId: string,
    payload: BankMovementWritePayload,
  ): Promise<BankMovement | null> => {
    const bankMovementPayload: Record<string, unknown> = {
      date: payload.date ?? null,
      deposit_date: payload.depositDate ?? null,
      issue_date: payload.issueDate ?? null,
      label: payload.label ?? null,
      bank_account_id: payload.accountId ?? null,
      payment_method: payload.paymentMode ?? null,
      payment_number: payload.paymentNumber ?? null,
      remittance_number: payload.remittanceNumber ?? null,
      debit: payload.debit ?? null,
      credit: payload.credit ?? null,
    };
    if ("comptaPieceSignedId" in payload) {
      bankMovementPayload.compta_piece = payload.comptaPieceSignedId
        ? { signed_id: payload.comptaPieceSignedId }
        : null;
    }

    const response = await axiosInstance.put<BankMovementResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.UPDATE_BANK_MOVEMENT(bankMovementId)),
      { bank_movement: bankMovementPayload },
    );

    if (!response.data.bank_movement) return null;
    return fromApiBankMovement(response.data.bank_movement);
  },

  deleteBankMovement: async (
    bankMovementId: string,
  ): Promise<void> => {
    await axiosInstance.delete(
      getApiUrl(ACCOUNTING_ENDPOINTS.DELETE_BANK_MOVEMENT(bankMovementId)),
    );
  },

  createBankMovement: async (
    payload: BankMovementWritePayload = {},
  ): Promise<BankMovement | null> => {
    const bankMovementPayload: Record<string, unknown> = {
      date: payload.date ?? null,
      deposit_date: payload.depositDate ?? null,
      issue_date: payload.issueDate ?? null,
      label: payload.label ?? null,
      bank_account_id: payload.accountId ?? null,
      payment_method: payload.paymentMode ?? null,
      payment_number: payload.paymentNumber ?? null,
      remittance_number: payload.remittanceNumber ?? null,
      debit: payload.debit ?? null,
      credit: payload.credit ?? null,
    };
    if ("comptaPieceSignedId" in payload) {
      bankMovementPayload.compta_piece = payload.comptaPieceSignedId
        ? { signed_id: payload.comptaPieceSignedId }
        : null;
    }

    const response = await axiosInstance.post<BankMovementResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.CREATE_BANK_MOVEMENT),
      { bank_movement: bankMovementPayload },
    );

    if (!response.data.bank_movement) return null;
    return fromApiBankMovement(response.data.bank_movement);
  },

  listLedgerAllocations: async (
    params: LedgerAllocationsQueryParams,
  ): Promise<LedgerAllocationsListResult> => {
    const searchParams = buildSearchParams(
      {
        page: params.page ?? 1,
        per_page: params.limit ?? 50,
        q: ledgerAllocationsSearchQuery(params),
      },
      { defaults: { page: 1, limit: 50 } },
    );

    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.LIST_LEDGER_ALLOCATIONS(params.bankMovementId)}${queryString ? `?${queryString}` : ""}`,
    );

    const response = await axiosInstance.get<
      ComptaMouvementAffectationsListResponse | JsonApiListResponse<ComptaMouvementAffectationApiResource>
    >(url);

    if ("ledger_allocations" in response.data || "compta_mouvement_affectations" in response.data) {
      return {
        ledgerAllocations: (response.data.ledger_allocations ?? response.data.compta_mouvement_affectations ?? []).map(fromApiLedgerAllocation),
        metadata: response.data.metadata,
      };
    }

    const jsonApiData = response.data as JsonApiListResponse<ComptaMouvementAffectationApiResource>;
    return {
      ledgerAllocations: mapJsonApiLedgerAllocations(jsonApiData),
      metadata: toMetadata(jsonApiData.meta),
    };
  },

  // Catégories portées par les écritures que retournent les autres filtres de
  // la liste (le filtre Catégorie lui-même est ignoré côté API).
  getLedgerOperationTypeFacets: async (
    params: LedgerAllocationsQueryParams,
  ): Promise<LedgerOperationTypeFacets> => {
    const searchParams = buildSearchParams({ q: ledgerAllocationsSearchQuery(params) });
    const queryString = searchParams.toString();
    const response = await axiosInstance.get<{
      operation_types: Array<LedgerAllocationOperationType & { id: string | number }>
      uncategorized_count: number
    }>(getApiUrl(`${ACCOUNTING_ENDPOINTS.LEDGER_OPERATION_TYPE_FACETS}${queryString ? `?${queryString}` : ""}`));
    return {
      operationTypes: (response.data.operation_types ?? []).map((item) => ({ ...item, id: String(item.id) })),
      uncategorizedCount: response.data.uncategorized_count ?? 0,
    };
  },

  // ADM-736 : déclenche l'export XLSX asynchrone du grand livre des stocks. On
  // renvoie au backend exactement le même `q` que la liste pour que le fichier
  // corresponde aux lignes filtrées à l'écran.
  exportStockLedgerXlsx: async (
    params: LedgerAllocationsQueryParams,
  ): Promise<JobServiceResponse> => {
    const response = await axiosInstance.post<JobServiceResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.EXPORT_LEDGER_ALLOCATIONS_XLSX),
      {
        q: {
          label_cont: params.label?.trim() || undefined,
          date_gteq: params.dateGteq,
          date_lteq: params.dateLteq,
          type_movement_eq: params.movementTypeEq,
          stock: params.stockOnly ? true : undefined,
          resolved_bank_account_id_in: params.resolvedBankAccountIds,
          source_module_cont: params.sourceModuleEq,
          activite_achats_famille_id_in: params.activitePurchaseFamilyIds,
          activite_achats_reference_id_in: params.activitePurchaseReferenceIds,
        },
      },
    );
    return response.data;
  },

  listLedgerAllocationOperationTypes: async (
    params: LedgerAllocationOperationTypesQueryParams,
  ): Promise<LedgerAllocationOperationType[]> => {
    const searchParams = new URLSearchParams();
    if (params.budgetType) {
      searchParams.append("type_budget", params.budgetType);
      searchParams.append("code", params.budgetType);
    }
    if (params.actif !== undefined) {
      searchParams.append("actif", String(params.actif));
    }

    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.LIST_LEDGER_ALLOCATION_OPERATION_TYPES(params.bankMovementId)}${queryString ? `?${queryString}` : ""}`,
    );

    const response = await axiosInstance.get<LedgerAllocationOperationTypesListResponse>(url);
    return (response.data.operation_types ?? []).map((item) => ({
      id: String(item.id),
      name: item.name ?? item.nom ?? "",
      code: item.code ?? null,
      actif: item.actif ?? true,
      nature: item.nature,
      // Sans ce champ, un compte saisi depuis la page Budgets n'apparaissait
      // pas dans le drawer DRW-CATMGR : le mapping le jetait.
      numero_compte: item.numero_compte ?? null,
      imported_from_pcg: item.imported_from_pcg ?? false,
      ledger_allocations_count: item.ledger_allocations_count ?? 0,
      categorization_rules_count: item.categorization_rules_count ?? 0,
    }));
  },

  createLedgerAllocationOperationType: async (
    params: LedgerAllocationOperationTypesQueryParams & { name: string, nature?: "expense" | "revenue" },
  ): Promise<LedgerAllocationOperationType | null> => {
    const response = await axiosInstance.post<LedgerAllocationOperationTypeResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.CREATE_LEDGER_ALLOCATION_OPERATION_TYPE(params.bankMovementId)),
      {
        name: params.name,
        type_budget: params.budgetType ?? undefined,
        nature: params.nature,
      },
    );

    if (!response.data.operation_type) return null;
    return {
      id: String(response.data.operation_type.id),
      name: response.data.operation_type.name ?? response.data.operation_type.nom ?? "",
      code: response.data.operation_type.code ?? null,
      actif: response.data.operation_type.actif ?? true,
      nature: response.data.operation_type.nature,
    };
  },

  updateLedgerAllocationOperationType: async (
    params: LedgerAllocationOperationTypesQueryParams & {
      operationTypeId: string
      name?: string
      actif?: boolean
      numeroCompte?: string | null
    },
  ): Promise<LedgerAllocationOperationType | null> => {
    const response = await axiosInstance.patch<LedgerAllocationOperationTypeResponse>(
      getApiUrl(
        ACCOUNTING_ENDPOINTS.UPDATE_LEDGER_ALLOCATION_OPERATION_TYPE(params.bankMovementId, params.operationTypeId),
      ),
      {
        name: params.name,
        actif: params.actif,
        // Vide = effacé : le compte comptable est facultatif.
        ...(params.numeroCompte !== undefined ? { numero_compte: params.numeroCompte ?? "" } : {}),
      },
    );

    if (!response.data.operation_type) return null;
    return {
      id: String(response.data.operation_type.id),
      name: response.data.operation_type.name ?? response.data.operation_type.nom ?? "",
      code: response.data.operation_type.code ?? null,
      actif: response.data.operation_type.actif ?? true,
    };
  },

  deleteLedgerAllocationOperationType: async (
    params: LedgerAllocationOperationTypesQueryParams & { operationTypeId: string },
  ): Promise<void> => {
    await axiosInstance.delete(
      getApiUrl(
        ACCOUNTING_ENDPOINTS.DELETE_LEDGER_ALLOCATION_OPERATION_TYPE(params.bankMovementId, params.operationTypeId),
      ),
    );
  },

  mergeLedgerAllocationOperationType: async (
    params: { bankMovementId: string, operationTypeId: string, targetOperationTypeId: string },
  ): Promise<LedgerAllocationOperationType | null> => {
    const response = await axiosInstance.post<LedgerAllocationOperationTypeResponse>(
      getApiUrl(
        ACCOUNTING_ENDPOINTS.MERGE_LEDGER_ALLOCATION_OPERATION_TYPE(params.bankMovementId, params.operationTypeId),
      ),
      { target_operation_type_id: params.targetOperationTypeId },
    );
    if (!response.data.operation_type) return null;
    return {
      id: String(response.data.operation_type.id),
      name: response.data.operation_type.name ?? response.data.operation_type.nom ?? "",
      code: response.data.operation_type.code ?? null,
      actif: response.data.operation_type.actif ?? true,
    };
  },

  listLedgerAllocationCategorizationRules: async (
    params: LedgerAllocationCategorizationRulesQueryParams = {},
  ): Promise<LedgerAllocationCategorizationRule[]> => {
    const searchParams = new URLSearchParams();
    if (params.budgetType) searchParams.append("budget_type", params.budgetType);
    if (params.active !== undefined) searchParams.append("active", String(params.active));
    if (params.query?.trim()) searchParams.append("query", params.query.trim());

    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.LIST_LEDGER_ALLOCATION_CATEGORIZATION_RULES}${queryString ? `?${queryString}` : ""}`,
    );

    const response = await axiosInstance.get<LedgerAllocationCategorizationRulesListResponse>(url);
    return (response.data.rules ?? []).map(fromApiCategorizationRule);
  },

  createLedgerAllocationCategorizationRule: async (
    payload: LedgerAllocationCategorizationRuleCreatePayload,
  ): Promise<LedgerAllocationCategorizationRule | null> => {
    const response = await axiosInstance.post<LedgerAllocationCategorizationRuleResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.CREATE_LEDGER_ALLOCATION_CATEGORIZATION_RULE),
      {
        ledger_allocation_categorization_rule: {
          name: payload.name,
          budget_type: payload.budgetType,
          operation_type_id: payload.operationTypeId ?? null,
          active: payload.active ?? true,
          conditions: payload.conditions,
          position: payload.position ?? 0,
        },
      },
    );

    return response.data.rule ? fromApiCategorizationRule(response.data.rule) : null;
  },

  updateLedgerAllocationCategorizationRule: async (
    payload: LedgerAllocationCategorizationRuleUpdatePayload,
  ): Promise<LedgerAllocationCategorizationRule | null> => {
    const response = await axiosInstance.put<LedgerAllocationCategorizationRuleResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.UPDATE_LEDGER_ALLOCATION_CATEGORIZATION_RULE(payload.id)),
      {
        ledger_allocation_categorization_rule: {
          name: payload.name,
          budget_type: payload.budgetType,
          operation_type_id: payload.operationTypeId,
          active: payload.active,
          conditions: payload.conditions,
          position: payload.position,
        },
      },
    );

    return response.data.rule ? fromApiCategorizationRule(response.data.rule) : null;
  },

  deleteLedgerAllocationCategorizationRule: async (id: string): Promise<void> => {
    await axiosInstance.delete(getApiUrl(ACCOUNTING_ENDPOINTS.DELETE_LEDGER_ALLOCATION_CATEGORIZATION_RULE(id)));
  },

  createLedgerAllocation: async (
    payload: LedgerAllocationWritePayload,
  ): Promise<LedgerAllocation> => {
    const response = await axiosInstance.post<
      ComptaMouvementAffectationResponse | JsonApiSingleResponse<ComptaMouvementAffectationApiResource>
    >(
      getApiUrl(ACCOUNTING_ENDPOINTS.CREATE_LEDGER_ALLOCATION(payload.bankMovementId)),
      toApiLedgerAllocationPayload(payload),
    );

    if ("ledger_allocation" in response.data && response.data.ledger_allocation) {
      return fromApiLedgerAllocation(response.data.ledger_allocation);
    }

    if ("compta_mouvement_affectation" in response.data && response.data.compta_mouvement_affectation) {
      return fromApiLedgerAllocation(response.data.compta_mouvement_affectation);
    }

    return mapJsonApiLedgerAllocation(response.data as JsonApiSingleResponse<ComptaMouvementAffectationApiResource>);
  },

  moveLedgerAllocation: async (id: string, targetBankMovementId: string): Promise<void> => {
    // A move changes only the parent. The full edit mapper clears omitted fields.
    await axiosInstance.put(
      getApiUrl(ACCOUNTING_ENDPOINTS.UPDATE_LEDGER_ALLOCATION(id)),
      { ledger_allocation: { bank_movement_id: targetBankMovementId } },
    );
  },

  updateLedgerAllocation: async (
    id: string,
    payload: LedgerAllocationWritePayload,
  ): Promise<LedgerAllocation> => {
    const response = await axiosInstance.put<
      ComptaMouvementAffectationResponse | JsonApiSingleResponse<ComptaMouvementAffectationApiResource>
    >(
      getApiUrl(ACCOUNTING_ENDPOINTS.UPDATE_LEDGER_ALLOCATION(id)),
      toApiLedgerAllocationPayload(payload),
    );

    if ("ledger_allocation" in response.data && response.data.ledger_allocation) {
      return fromApiLedgerAllocation(response.data.ledger_allocation);
    }

    if ("compta_mouvement_affectation" in response.data && response.data.compta_mouvement_affectation) {
      return fromApiLedgerAllocation(response.data.compta_mouvement_affectation);
    }

    return mapJsonApiLedgerAllocation(response.data as JsonApiSingleResponse<ComptaMouvementAffectationApiResource>);
  },

  discardTemporaryAllocation: async (id: string): Promise<void> => {
    try {
      await axiosInstance.delete(getApiUrl(ACCOUNTING_ENDPOINTS.DELETE_LEDGER_ALLOCATION(id)), { params: { temporary_only: true } });
    }
    catch (error) {
      if (!isAxiosError(error) || error.response?.status !== 404) throw error;
      // A previous close or the expiry worker already removed this draft.
    }
  },

  deleteLedgerAllocation: async (id: string): Promise<void> => {
    await axiosInstance.delete(getApiUrl(ACCOUNTING_ENDPOINTS.DELETE_LEDGER_ALLOCATION(id)));
  },

  listStockCategories: async (bankMovementId: string): Promise<string[]> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.LIST_STOCK_CATEGORIES(bankMovementId));
    const response = await axiosInstance.get<StockCategoriesListResponse>(url);
    return response.data.stock_categories ?? [];
  },

  listStockFamilies: async (bankMovementId: string, category?: string): Promise<StockFamily[]> => {
    const searchParams = new URLSearchParams();
    if (category) searchParams.append("category", category);

    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.LIST_STOCK_FAMILIES(bankMovementId)}${queryString ? `?${queryString}` : ""}`,
    );

    const response = await axiosInstance.get<StockFamiliesListResponse>(url);
    return (response.data.stock_families ?? []).map((f) => ({
      id: String(f.id),
      name: f.name,
      category: f.category ?? null,
    }));
  },

  listStockReferences: async (bankMovementId: string, familyId?: string): Promise<StockReference[]> => {
    const searchParams = new URLSearchParams();
    if (familyId) searchParams.append("family_id", familyId);

    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.LIST_STOCK_REFERENCES(bankMovementId)}${queryString ? `?${queryString}` : ""}`,
    );

    const response = await axiosInstance.get<StockReferencesListResponse>(url);
    return (response.data.stock_references ?? []).map((r) => ({
      id: String(r.id),
      name: r.name,
      stockValue: r.stock_value ?? null,
      familyName: r.family_name ?? null,
    }));
  },

  // ADM-609: organization-scoped variants — not tied to a bank movement,
  // used by the Grand Livre filter popovers.
  listLedgerStockFamilies: async (category?: string, stockOnly?: boolean): Promise<StockFamily[]> => {
    const searchParams = new URLSearchParams();
    if (category) searchParams.append("category", category);
    if (stockOnly) searchParams.append("stock_only", "true");

    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.LIST_LEDGER_STOCK_FAMILIES}${queryString ? `?${queryString}` : ""}`,
    );

    const response = await axiosInstance.get<StockFamiliesListResponse>(url);
    return (response.data.stock_families ?? []).map((f) => ({
      id: String(f.id),
      name: f.name,
      category: f.category ?? null,
    }));
  },

  listLedgerStockReferences: async (
    familyId?: string,
    category?: string,
    stockOnly?: boolean,
  ): Promise<StockReference[]> => {
    const searchParams = new URLSearchParams();
    if (familyId) searchParams.append("family_id", familyId);
    if (category) searchParams.append("category", category);
    if (stockOnly) searchParams.append("stock_only", "true");

    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.LIST_LEDGER_STOCK_REFERENCES}${queryString ? `?${queryString}` : ""}`,
    );

    const response = await axiosInstance.get<StockReferencesListResponse>(url);
    return (response.data.stock_references ?? []).map((r) => ({
      id: String(r.id),
      name: r.name,
      stockValue: r.stock_value ?? null,
      familyName: r.family_name ?? null,
    }));
  },

  searchRefundEmployees: async (
    query: string,
  ): Promise<RefundEmployeeSearchResult[]> => {
    const searchParams = new URLSearchParams();
    searchParams.append("q", query);

    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.REFUND_EMPLOYEES_SEARCH}?${searchParams.toString()}`,
    );

    const response = await axiosInstance.get<{
      employees: Array<{
        id: number
        first_name: string
        last_name: string
        site_name: string | null
        staff_number: string | null
      }>
    }>(url);

    return (response.data.employees ?? []).map((e) => ({
      id: e.id,
      firstName: e.first_name,
      lastName: e.last_name,
      siteName: e.site_name ?? null,
      staffNumber: e.staff_number ?? null,
    }));
  },

  getRefundOperations: async (
    clientId: number,
    includeSettled?: boolean,
  ): Promise<RefundSearchResult[]> => {
    const searchParams = new URLSearchParams();
    searchParams.append("client_id", String(clientId));
    if (includeSettled) searchParams.append("include_settled", "true");

    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.REFUND_OPERATIONS}?${searchParams.toString()}`,
    );

    const response = await axiosInstance.get<{
      refunds: Array<{
        id: number
        status_name: string | null
        payment_blocked?: boolean
        amount_retained: number | null
        allocation_balance: number | null
        created_at: string | null
        operations: Array<{
          id: number
          label: string
          category: string | null
          debit: number | null
          credit: number | null
          allocation_balance: number | null
        }>
      }>
    }>(url);

    return (response.data.refunds ?? []).map((r) => ({
      id: r.id,
      statusName: r.status_name ?? null,
      // Défaut à false : une API plus ancienne qui n'envoie pas le champ ne doit
      // pas bloquer tous les rapprochements. Le back refuse de toute façon.
      paymentBlocked: r.payment_blocked ?? false,
      amountRetained: r.amount_retained ?? null,
      allocationBalance: r.allocation_balance ?? null,
      createdAt: r.created_at ?? null,
      operations: (r.operations ?? []).map((op) => ({
        id: op.id,
        label: op.label,
        category: op.category ?? null,
        debit: op.debit ?? null,
        credit: op.credit ?? null,
        allocationBalance: op.allocation_balance ?? null,
      })),
    }));
  },

  searchEcommerceOrders: async (
    query: string,
    includeSettled?: boolean,
  ): Promise<EcommerceOrderSearchResult[]> => {
    const searchParams = new URLSearchParams();
    searchParams.append("q", query);
    if (includeSettled) searchParams.append("include_settled", "true");

    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.ECOMMERCE_ORDERS_SEARCH}?${searchParams.toString()}`,
    );

    const response = await axiosInstance.get<{
      ecommerce_orders: Array<{
        id: number
        ref: string
        total: number | null
        allocation_balance: number | null
        created_at: string | null
        status_name: string | null
        client_name: string | null
        client_site: string | null
        operations: Array<{
          id: number
          label: string
          category: string | null
          beneficiary: string | null
          debit: number | null
          credit: number | null
          allocation_balance: number | null
        }>
      }>
    }>(url);

    return (response.data.ecommerce_orders ?? []).map((order) => ({
      id: order.id,
      ref: order.ref,
      total: order.total ?? null,
      allocationBalance: order.allocation_balance ?? null,
      createdAt: order.created_at ?? null,
      statusName: order.status_name ?? null,
      clientName: order.client_name ?? null,
      clientSite: order.client_site ?? null,
      operations: (order.operations ?? []).map((op) => ({
        id: op.id,
        label: op.label,
        category: op.category ?? null,
        beneficiary: op.beneficiary ?? null,
        debit: op.debit ?? null,
        credit: op.credit ?? null,
        allocationBalance: op.allocation_balance ?? null,
      })),
    }));
  },

  // ── Settlements ──────────────────────────────────────────

  getSettlement: async (id: string): Promise<Settlement> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.SHOW_SETTLEMENT(id));
    const response = await axiosInstance.get<SettlementResponse>(url);
    if (!response.data.settlement) throw new Error("Réponse invalide");
    return fromApiSettlement(response.data.settlement);
  },

  listSettlements: async (params: SettlementsQueryParams = {}): Promise<SettlementsListResult> => {
    const searchParams = buildSearchParams(
      {
        page: params.page,
        per_page: params.limit,
        q: {
          s: params.sorts,
          year_eq: params.yearEq,
          deep_smart_query: params.allocationSearch?.trim() || undefined,
          date_gteq: params.dateGteq,
          date_lteq: params.dateLteq,
          payment_method_in: params.paymentMethodIn,
          bank_account_id_in: params.bankAccountIds,
          deposit_status_eq: params.depositStatusEq,
          amount_eq: params.amountEq,
          amount_gt: params.amountGt,
          amount_lt: params.amountLt,
          amount_not_eq: params.amountNotEq,
          amount_gteq: params.amountGteq,
          amount_lteq: params.amountLteq,
          bank_movement_id_eq: params.bankMovementIdEq,
          bank_movement_id_null: params.bankMovementIdNull,
          payment_method_not_eq: params.paymentMethodNotEq,
          allocation_date_gteq: params.allocationDateGteq || undefined,
          allocation_date_lteq: params.allocationDateLteq || undefined,
        },
      },
      { defaults: { page: 1, limit: 50 } },
    );
    if (params.context) searchParams.append("context", params.context);

    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.LIST_SETTLEMENTS}${queryString ? `?${queryString}` : ""}`,
    );

    const response = await axiosInstance.get<SettlementsListResponse>(url);

    return {
      settlements: (response.data.settlements ?? []).map(fromApiSettlement),
      metadata: {
        current_page: response.data.meta?.current_page ?? 1,
        total_pages: response.data.meta?.total_pages ?? 1,
        total_count: response.data.meta?.total_count ?? 0,
        total_items: response.data.meta?.total_count ?? 0,
        per_page: response.data.meta?.per_page ?? 50,
      },
    };
  },

  createSettlement: async (payload: SettlementWritePayload): Promise<Settlement> => {
    const settlementPayload: Record<string, unknown> = {
      date: payload.date ?? null,
      number: payload.number ?? null,
      issuer: payload.issuer ?? null,
      payment_method: payload.paymentMethod ?? null,
      amount: payload.amount ?? null,
      deferred_date: payload.deferredDate ?? null,
      bank_account_id: payload.bankAccountId ?? null,
      client_id: payload.clientId ?? null,
    };
    if (payload.bankMovementId !== null && payload.bankMovementId !== undefined) {
      settlementPayload.compta_mouvement_id = payload.bankMovementId;
    }

    const response = await axiosInstance.post<SettlementResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.CREATE_SETTLEMENT),
      { settlement: settlementPayload },
    );

    if (!response.data.settlement) throw new Error("Réponse invalide");
    return fromApiSettlement(response.data.settlement);
  },

  updateSettlement: async (id: string, payload: SettlementWritePayload): Promise<Settlement> => {
    const settlementPayload: Record<string, unknown> = {};
    if (payload.date !== undefined) settlementPayload.date = payload.date;
    if (payload.number !== undefined) settlementPayload.number = payload.number;
    if (payload.issuer !== undefined) settlementPayload.issuer = payload.issuer;
    if (payload.paymentMethod !== undefined) settlementPayload.payment_method = payload.paymentMethod;
    if (payload.amount !== undefined) settlementPayload.amount = payload.amount;
    if (payload.deferredDate !== undefined) settlementPayload.deferred_date = payload.deferredDate;
    if (payload.bankAccountId !== undefined) settlementPayload.bank_account_id = payload.bankAccountId;
    if (payload.clientId !== undefined) settlementPayload.client_id = payload.clientId;
    if (payload.bankMovementId !== undefined) settlementPayload.compta_mouvement_id = payload.bankMovementId;

    const response = await axiosInstance.put<SettlementResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.UPDATE_SETTLEMENT(id)),
      { settlement: settlementPayload },
    );

    if (!response.data.settlement) throw new Error("Réponse invalide");
    return fromApiSettlement(response.data.settlement);
  },

  createSettlementWithMovement: async (payload: {
    settlement: SettlementWritePayload
    bankMovement: BankMovementWritePayload
  }): Promise<Settlement> => {
    const response = await axiosInstance.post<SettlementResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.CREATE_SETTLEMENT_WITH_MOVEMENT),
      {
        settlement: {
          date: payload.settlement.date ?? null,
          number: payload.settlement.number ?? null,
          issuer: payload.settlement.issuer ?? null,
          payment_method: payload.settlement.paymentMethod ?? null,
          amount: payload.settlement.amount ?? null,
          deferred_date: payload.settlement.deferredDate ?? null,
          bank_account_id: payload.settlement.bankAccountId ?? null,
          client_id: payload.settlement.clientId ?? null,
        },
        bank_movement: {
          date: payload.bankMovement.date ?? null,
          deposit_date: payload.bankMovement.depositDate ?? null,
          issue_date: payload.bankMovement.issueDate ?? null,
          label: payload.bankMovement.label ?? null,
          bank_account_id: payload.bankMovement.accountId ?? null,
          payment_method: payload.bankMovement.paymentMode ?? null,
          payment_number: payload.bankMovement.paymentNumber ?? null,
          remittance_number: payload.bankMovement.remittanceNumber ?? null,
          debit: payload.bankMovement.debit ?? null,
          credit: payload.bankMovement.credit ?? null,
        },
      },
    );

    if (!response.data.settlement) throw new Error("Réponse invalide");
    return fromApiSettlement(response.data.settlement);
  },

  createSettlementMovement: async (settlementId: string): Promise<Settlement> => {
    const response = await axiosInstance.post<SettlementResponse>(
      getApiUrl(ACCOUNTING_ENDPOINTS.CREATE_SETTLEMENT_MOVEMENT(settlementId)),
    );
    if (!response.data.settlement) throw new Error("Réponse invalide");
    return fromApiSettlement(response.data.settlement);
  },

  deleteSettlement: async (id: string, options?: { keepAllocations?: boolean }): Promise<void> => {
    const params = options?.keepAllocations ? { keep_allocations: true } : undefined;
    await axiosInstance.delete(getApiUrl(ACCOUNTING_ENDPOINTS.DELETE_SETTLEMENT(id)), { params });
  },

  listSettlementAccounts: async (params: { context?: string } = {}): Promise<BankMovementAccount[]> => {
    const searchParams = new URLSearchParams();
    if (params.context) searchParams.append("context", params.context);
    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.LIST_SETTLEMENT_ACCOUNTS}${queryString ? `?${queryString}` : ""}`,
    );
    const response = await axiosInstance.get<BankMovementAccountsListResponse>(url);

    return (response.data.bank_accounts ?? []).map((account) => ({
      id: String(account.id),
      name: account.name,
      budgetType: account.budget_type ?? null,
      accountType: (account.account_type as BankMovementAccount["accountType"]) ?? null,
    }));
  },

  listSettlementPaymentMethods: async (params: { context?: string } = {}): Promise<string[]> => {
    const searchParams = new URLSearchParams();
    if (params.context) searchParams.append("context", params.context);
    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${ACCOUNTING_ENDPOINTS.LIST_SETTLEMENT_PAYMENT_METHODS}${queryString ? `?${queryString}` : ""}`,
    );
    const response = await axiosInstance.get<SettlementPaymentMethodsListResponse>(url);
    return response.data.payment_methods ?? [];
  },

  listSettlementAllocations: async (settlementId: string): Promise<LedgerAllocation[]> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.LIST_SETTLEMENT_ALLOCATIONS(settlementId));
    const response = await axiosInstance.get<{ ledger_allocations: ComptaMouvementAffectationApiResource[] }>(url);
    return (response.data.ledger_allocations ?? []).map(fromApiLedgerAllocation);
  },

  createRemise: async (payload: CreateRemisePayload): Promise<CreateRemiseResponse> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.CREATE_REMISE);
    const response = await axiosInstance.post<{ bank_movement_id: string, updated_count: number }>(url, {
      settlement_ids: payload.settlementIds,
      bank_account_id: payload.bankAccountId,
      deposit_date: payload.depositDate ?? null,
      label: payload.label ?? null,
      remise_number: payload.remiseNumber ?? null,
    });
    return {
      bankMovementId: response.data.bank_movement_id,
      updatedCount: response.data.updated_count,
    };
  },

  getRemisePdf: async (bankMovementId: string): Promise<RemisePdfResponse> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.REMISE_PDF(bankMovementId));
    const response = await axiosInstance.get<{ file_base: string, type_file: string, name: string }>(url);
    return {
      fileBase: response.data.file_base,
      typeFile: response.data.type_file,
      name: response.data.name,
    };
  },

  // ── Sélection complète ──────────────────────────────────

  /**
   * Tous les mouvements que le filtre courant désigne, au-delà de la page
   * affichée : la case « tout sélectionner » du mode SEPA doit porter sur la
   * remise entière, pas sur les vingt lignes chargées. Pages de 500 (le
   * plafond de l'API), arrêt au-delà de `maxItems` pour ne pas aspirer une
   * organisation entière par erreur.
   */
  listBankMovementsForSelection: async (
    params: Omit<BankMovementsQueryParams, "page" | "limit">,
    maxItems = 5000,
  ): Promise<{ items: Array<{ id: string, debit: number | null, credit: number | null }>, truncated: boolean }> => {
    const pageSize = 500;
    const items: Array<{ id: string, debit: number | null, credit: number | null }> = [];
    let page = 1;
    let totalPages: number;
    do {
      const result = await accountingService.listBankMovements({ ...params, page, limit: pageSize });
      result.bankMovements.forEach((movement) => items.push({ id: movement.id, debit: movement.debit, credit: movement.credit }));
      totalPages = result.metadata?.total_pages ?? 1;
      page += 1;
    } while (page <= totalPages && items.length < maxItems);
    return { items: items.slice(0, maxItems), truncated: items.length > maxItems || page <= totalPages };
  },

  // ── SEPA Export ──────────────────────────────────────────

  sepaExport: async (payload: SepaExportPayload): Promise<SepaExportResponse> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.SEPA_EXPORT);
    const response = await axiosInstance.post<{
      file_base?: string
      type_file?: string
      name?: string
      bank_movement_id?: string
      sepa_remittance_id?: string
      error?: boolean
      error_message?: string
      error_details?: Array<{
        mouvement_id: string
        affectation_id: number
        salarie: string | null
        champ: string
        message: string
      }>
    }>(url, {
      bank_movement_ids: payload.bankMovementIds,
      process: payload.process,
      date_execution: payload.dateExecution,
      confirmed: payload.confirmed,
      payment_label: payload.paymentLabel,
    });
    if (response.data.error) {
      if (response.data.error_details) {
        const details: SepaValidationErrorDetail[] = response.data.error_details.map((d) => ({
          mouvementId: d.mouvement_id,
          affectationId: d.affectation_id,
          salarie: d.salarie,
          champ: d.champ,
          message: d.message,
        }));
        const err = new Error("Erreurs de validation SEPA");
        (err as SepaValidationError).details = details;
        throw err;
      }
      throw new Error(response.data.error_message || "Erreur lors de la génération SEPA");
    }
    return {
      fileBase: response.data.file_base!,
      typeFile: response.data.type_file!,
      name: response.data.name!,
      bankMovementId: response.data.bank_movement_id ?? undefined,
      sepaRemittanceId: response.data.sepa_remittance_id ?? undefined,
    };
  },

  // ── SEPA remittances (YTI-1732) ──────────────────────────

  listSepaRemittances: async (params: SepaRemittancesQueryParams = {}): Promise<SepaRemittancesListResult> => {
    const searchParams = buildSearchParams(
      { page: params.page, per_page: params.limit },
      { usePerPage: true, defaults: { page: 1, limit: 50 } },
    );
    if (params.status) searchParams.append("status", params.status);
    if (params.process) searchParams.append("process", params.process);
    if (params.withIntegrity) searchParams.append("with_integrity", "true");
    const url = `${getApiUrl(ACCOUNTING_ENDPOINTS.LIST_SEPA_REMITTANCES)}?${searchParams.toString()}`;
    const response = await axiosInstance.get<SepaRemittancesListResponse>(url);
    return {
      sepaRemittances: (response.data.sepa_remittances ?? []).map(fromApiSepaRemittance),
      metadata: response.data.metadata,
    };
  },

  getSepaRemittance: async (id: string): Promise<SepaRemittance> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.SHOW_SEPA_REMITTANCE(id));
    const response = await axiosInstance.get<{ sepa_remittance: SepaRemittanceApiResource }>(url);
    return fromApiSepaRemittance(response.data.sepa_remittance);
  },

  // Réédition à l'identique : le fichier tel qu'il a été produit, sans relire la base.
  downloadSepaRemittanceFile: async (id: string, file: "xml" | "csv" = "xml", version?: number): Promise<SepaRemittanceFile> => {
    const url = `${getApiUrl(ACCOUNTING_ENDPOINTS.DOWNLOAD_SEPA_REMITTANCE(id))}?file=${file}${version ? `&version=${version}` : ""}`;
    const response = await axiosInstance.get<{ file_base: string, type_file: string, name: string }>(url);
    return {
      fileBase: response.data.file_base,
      typeFile: response.data.type_file,
      name: response.data.name,
    };
  },

  // Régénération avant dépôt seulement. Les anciennes API peuvent encore demander une confirmation.
  regenerateSepaRemittance: async (
    id: string,
    options: { confirmed?: boolean, dateExecution?: string | null, paymentLabel?: string } = {},
  ): Promise<SepaRemittanceRegenerateResult> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.REGENERATE_SEPA_REMITTANCE(id));
    try {
      const response = await axiosInstance.post<{
        file_base: string
        type_file: string
        name: string
        sepa_remittance: SepaRemittanceApiResource
      }>(url, {
        confirmed: options.confirmed ? true : undefined,
        date_execution: options.dateExecution ?? undefined,
        payment_label: options.paymentLabel,
      });
      return {
        requiresConfirmation: false,
        file: { fileBase: response.data.file_base, typeFile: response.data.type_file, name: response.data.name },
        sepaRemittance: fromApiSepaRemittance(response.data.sepa_remittance),
      };
    }
    catch (error) {
      const data = (error as { response?: { status?: number, data?: { requires_confirmation?: boolean, reason?: string, message?: string } } })
        .response?.data;
      if (data?.requires_confirmation) {
        return { requiresConfirmation: true, reason: data.reason ?? "banked", message: data.message ?? "" };
      }
      throw error;
    }
  },

  bankTreatment: async (id: string, input: SepaBankTreatmentInput, apply = false): Promise<SepaBankTreatmentPreview> => {
    const action = apply ? "apply_bank_treatment" : "preview_bank_treatment";
    const response = await axiosInstance.post<{ treatment: SepaBankTreatmentPreview }>(`${getApiUrl(ACCOUNTING_ENDPOINTS.SHOW_SEPA_REMITTANCE(id))}/${action}`, input);
    return response.data.treatment;
  },

  recordSepaRemittanceEvent: async (id: string, input: SepaRemittanceEventInput): Promise<SepaRemittance> => {
    const url = `${getApiUrl(ACCOUNTING_ENDPOINTS.SHOW_SEPA_REMITTANCE(id))}/record_event`;
    const response = await axiosInstance.post<{ sepa_remittance: SepaRemittanceApiResource }>(url, {
      kind: input.kind, version: input.version, reference: input.reference, note: input.note,
      occurred_at: input.occurredAt, idempotency_key: input.idempotencyKey,
    });
    return fromApiSepaRemittance(response.data.sepa_remittance);
  },

  rollbackSepaRemittance: async (id: string): Promise<SepaRemittanceRollbackResult> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.ROLLBACK_SEPA_REMITTANCE(id));
    const response = await axiosInstance.post<{
      restored_movement_ids: number[]
      destroyed_movement_id: number | null
      sepa_remittance: SepaRemittanceApiResource
    }>(url, {});
    return {
      restoredMovementIds: response.data.restored_movement_ids ?? [],
      destroyedMovementId: response.data.destroyed_movement_id ?? null,
      sepaRemittance: fromApiSepaRemittance(response.data.sepa_remittance),
    };
  },

  sepaEligibility: async (bankAccountId: number): Promise<SepaEligibility> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.SEPA_ELIGIBILITY);
    const response = await axiosInstance.get<{
      eligible_count: number
      excluded: SepaEligibility["excluded"]
      to_fix?: SepaEligibility["toFix"]
    }>(url, { params: { bank_account_id: bankAccountId } });
    return { eligibleCount: response.data.eligible_count, excluded: response.data.excluded ?? [], toFix: response.data.to_fix ?? [] };
  },

  sepaValidate: async (bankMovementIds: string[]): Promise<SepaValidateResult> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.SEPA_VALIDATE);
    const response = await axiosInstance.post<{
      valid: boolean
      errors: Array<{
        mouvement_id: string
        affectation_id: number | null
        salarie: string | null
        employee_id: number | null
        champ: string
        message: string
      }>
      message?: string
    }>(url, { bank_movement_ids: bankMovementIds });
    return {
      valid: response.data.valid,
      errors: response.data.errors.map((e) => ({
        mouvementId: e.mouvement_id,
        affectationId: e.affectation_id,
        salarie: e.salarie,
        employeeId: e.employee_id,
        champ: e.champ,
        message: e.message,
      })),
      message: response.data.message,
    };
  },

  // ── Synthesis ──────────────────────────────────────────

  getSynthesis: async (params: SynthesisQueryParams = {}): Promise<SynthesisResult> => {
    const searchParams = new URLSearchParams();
    if (params.year) searchParams.append("year", params.year);
    if (params.bankAccountId) searchParams.append("bank_account_id", params.bankAccountId);
    if (params.budgetType) searchParams.append("budget_type", params.budgetType);
    if (params.dateGteq) searchParams.append("date_gteq", params.dateGteq);
    if (params.dateLteq) searchParams.append("date_lteq", params.dateLteq);

    const qs = searchParams.toString();
    const url = getApiUrl(`${ACCOUNTING_ENDPOINTS.SYNTHESIS}${qs ? `?${qs}` : ""}`);
    const response = await axiosInstance.get<{
      synthesis: {
        budget_synthesis?: NetBudgetSynthesisApi
        year: string
        exercice: number
        exercice_start: string | null
        exercice_end: string | null
        covers_full_exercice?: boolean
        global_budget?: number | null
        allocated_budget?: number
        by_operation_type: Array<{
          operation_type_id: number | null
          operation_type_name: string
          budget_type?: CseBudgetType | null
          total_debit: number
          total_credit: number
          balance: number
          transaction_count: number
          amount?: number
          consumed?: number
          budget?: number | null
          delta?: number | null
          previous_consumed?: number
        }>
        monthly_breakdown: Array<{ month: number, total_debit: number, total_credit: number }>
        totals: {
          total_debit: number
          total_credit: number
          balance: number
          transaction_count: number
          amount?: number
          consumed?: number
          budget?: number | null
          delta?: number | null
          previous_consumed?: number
          previous_budget?: number | null
        }
        previous_exercice?: {
          exercice: number
          label: string
          start_date: string | null
          end_date: string | null
          consumed: number
          budget: number | null
          monthly_consumed?: Array<{ month: number, consumed: number }>
        } | null
      }
    }>(url);

    const data = response.data.synthesis;
    return applyNetBudgetSynthesis({
      year: data.year,
      exercice: data.exercice,
      exerciceStart: data.exercice_start,
      exerciceEnd: data.exercice_end,
      coversFullExercice: data.covers_full_exercice ?? true,
      globalBudget: data.global_budget ?? null,
      allocatedBudget: data.allocated_budget ?? 0,
      byOperationType: (data.by_operation_type ?? []).map((item) => ({
        operationTypeId: item.operation_type_id,
        operationTypeName: item.operation_type_name,
        budgetType: item.budget_type ?? null,
        totalDebit: item.total_debit,
        totalCredit: item.total_credit,
        balance: item.balance,
        transactionCount: item.transaction_count,
        // Recalculable côté front : évite un NaN si l'API n'est pas encore
        // déployée avec ADM-621 (dev local sur une API restée sur develop).
        amount: item.amount ?? (item.total_debit - item.total_credit),
        consumed: item.consumed ?? item.total_debit,
        budget: item.budget ?? null,
        delta: item.delta ?? null,
        previousConsumed: item.previous_consumed ?? 0,
      })),
      monthlyBreakdown: (data.monthly_breakdown ?? []).map((item) => ({
        month: item.month,
        totalDebit: item.total_debit,
        totalCredit: item.total_credit,
      })),
      totals: {
        totalDebit: data.totals.total_debit,
        totalCredit: data.totals.total_credit,
        balance: data.totals.balance,
        transactionCount: data.totals.transaction_count,
        amount: data.totals.amount ?? (data.totals.total_debit - data.totals.total_credit),
        consumed: data.totals.consumed ?? data.totals.total_debit,
        // `?? null` volontaire : sans ces champs (API pas encore déployée), il
        // faut lire « pas de budget », pas afficher un NaN.
        budget: data.totals.budget ?? null,
        delta: data.totals.delta ?? null,
        previousConsumed: data.totals.previous_consumed ?? 0,
        previousBudget: data.totals.previous_budget ?? null,
      },
      previousExercice: data.previous_exercice
        ? {
          exercice: data.previous_exercice.exercice,
          label: data.previous_exercice.label,
          startDate: data.previous_exercice.start_date,
          endDate: data.previous_exercice.end_date,
          consumed: data.previous_exercice.consumed,
          budget: data.previous_exercice.budget,
          monthlyConsumed: data.previous_exercice.monthly_consumed ?? [],
        }
        : null,
    }, data.budget_synthesis);
  },

  exportSynthesis: async (params: SynthesisQueryParams = {}): Promise<{ fileBase: string, typeFile: string, name: string }> => {
    const searchParams = new URLSearchParams();
    if (params.year) searchParams.append("year", params.year);
    if (params.bankAccountId) searchParams.append("bank_account_id", params.bankAccountId);
    if (params.budgetType) searchParams.append("budget_type", params.budgetType);
    if (params.dateGteq) searchParams.append("date_gteq", params.dateGteq);
    if (params.dateLteq) searchParams.append("date_lteq", params.dateLteq);

    const qs = searchParams.toString();
    const url = getApiUrl(`${ACCOUNTING_ENDPOINTS.SYNTHESIS_EXPORT}${qs ? `?${qs}` : ""}`);
    const response = await axiosInstance.get<{ file_base: string, type_file: string, name: string }>(url);
    return {
      fileBase: response.data.file_base,
      typeFile: response.data.type_file,
      name: response.data.name,
    };
  },

  getSynthesisDetail: async (params: SynthesisDetailQueryParams): Promise<SynthesisDetailResult> => {
    const searchParams = new URLSearchParams();
    if (params.year) searchParams.append("year", params.year);
    if (params.bankAccountId) searchParams.append("bank_account_id", params.bankAccountId);
    if (params.budgetType) searchParams.append("budget_type", params.budgetType);
    if (params.dateGteq) searchParams.append("date_gteq", params.dateGteq);
    if (params.dateLteq) searchParams.append("date_lteq", params.dateLteq);
    searchParams.append("category_name", params.categoryName);
    if (params.uncategorized) searchParams.append("uncategorized", "true");
    if (params.operationTypeId !== undefined) searchParams.append("operation_type_id", String(params.operationTypeId));
    if (params.page) searchParams.append("page", String(params.page));
    if (params.perPage) searchParams.append("per_page", String(params.perPage));

    const url = getApiUrl(`${ACCOUNTING_ENDPOINTS.SYNTHESIS_DETAIL}?${searchParams.toString()}`);
    const response = await axiosInstance.get<{
      allocations: Array<{
        id: number
        date: string | null
        libelle: string | null
        autre: string | null
        debit: number
        credit: number
        compta_mouvement_id: number | null
      }>
      metadata: {
        total_items: number
        current_page: number
        total_pages: number
        per_page: number
      }
    }>(url);

    return {
      allocations: (response.data.allocations ?? []).map((a) => ({
        id: a.id,
        date: a.date,
        libelle: a.libelle,
        autre: a.autre,
        debit: a.debit,
        credit: a.credit,
        bankMovementId: a.compta_mouvement_id,
      })),
      metadata: {
        totalItems: response.data.metadata.total_items,
        currentPage: response.data.metadata.current_page,
        totalPages: response.data.metadata.total_pages,
        perPage: response.data.metadata.per_page,
      },
    };
  },

  // ── Budgets prévisionnels (ADM-621) ─────────────────────────

  getAccountingBudgets: async (params: AccountingBudgetsQueryParams = {}): Promise<AccountingBudgetsResult> => {
    const searchParams = new URLSearchParams();
    if (params.exercice) searchParams.append("exercice", String(params.exercice));

    const qs = searchParams.toString();
    const url = getApiUrl(`${ACCOUNTING_ENDPOINTS.LIST_ACCOUNTING_BUDGETS}${qs ? `?${qs}` : ""}`);
    const response = await axiosInstance.get<{
      exercice: number
      fiscal_year: {
        closing_day: number
        closing_month: number
        calendar: boolean
        start_date: string
        end_date: string
        label: string
        selectable_exercices: number[]
      }
      envelopes: Record<string, AccountingBudgetApiPayload | null> | undefined
      allocated: Record<string, number> | undefined
      category_budgets: AccountingBudgetApiPayload[]
    }>(url);

    const data = response.data;
    return {
      exercice: data.exercice,
      fiscalYear: {
        closingDay: data.fiscal_year.closing_day,
        closingMonth: data.fiscal_year.closing_month,
        calendar: data.fiscal_year.calendar,
        startDate: data.fiscal_year.start_date,
        endDate: data.fiscal_year.end_date,
        label: data.fiscal_year.label,
        selectableExercices: data.fiscal_year.selectable_exercices ?? [],
      },
      envelopes: {
        ASC: data.envelopes?.ASC ? mapBudget(data.envelopes.ASC) : null,
        AEP: data.envelopes?.AEP ? mapBudget(data.envelopes.AEP) : null,
      },
      allocated: {
        ASC: data.allocated?.ASC ?? 0,
        AEP: data.allocated?.AEP ?? 0,
      },
      categoryBudgets: (data.category_budgets ?? []).map(mapBudget),
    };
  },

  // L'API upsert sur (exercice, catégorie) pour une répartition, sur
  // (exercice, type) pour une enveloppe. Le front édite une grille, il n'a pas
  // à savoir si la ligne existe déjà.
  upsertAccountingBudget: async (payload: AccountingBudgetUpsertPayload): Promise<AccountingBudget> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.UPSERT_ACCOUNTING_BUDGET);
    const response = await axiosInstance.post<{ budget: AccountingBudgetApiPayload }>(url, {
      budget: {
        exercice: payload.exercice,
        compta_type_operation_id: payload.operationTypeId ?? null,
        type_budget: payload.budgetType ?? null,
        montant: payload.montant,
        commentaire: payload.commentaire ?? null,
      },
    });
    return mapBudget(response.data.budget);
  },

  deleteAccountingBudget: async (id: number): Promise<void> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.DELETE_ACCOUNTING_BUDGET(id));
    await axiosInstance.delete(url);
  },

  // ── Paramètres comptables (ADM-621) ─────────────────────────

  getAccountingSettings: async (): Promise<AccountingSettings> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.ACCOUNTING_SETTINGS);
    const response = await axiosInstance.get<{ accounting_settings: AccountingSettingsApiPayload }>(url);
    return mapAccountingSettings(response.data.accounting_settings);
  },

  updateAccountingSettings: async (payload: AccountingSettingsUpdatePayload): Promise<AccountingSettings> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.ACCOUNTING_SETTINGS);
    const response = await axiosInstance.patch<{ accounting_settings: AccountingSettingsApiPayload }>(url, {
      // `confirm` : l'API répond 409 budgets_exist quand changer la clôture
      // déplacerait les exercices de budgets déjà saisis.
      ...(payload.confirm ? { confirm: true } : {}),
      accounting_settings: {
        closing_day: payload.closingDay,
        closing_month: payload.closingMonth,
      },
    });
    return mapAccountingSettings(response.data.accounting_settings);
  },

  // ── Catégories org-scoped (ADM-621) ─────────────────────────

  getOrgOperationTypes: async (params: { budgetType?: string | null, actif?: boolean } = {}): Promise<LedgerAllocationOperationType[]> => {
    const searchParams = new URLSearchParams();
    if (params.budgetType) searchParams.append("type_budget", params.budgetType);
    if (params.actif !== undefined) searchParams.append("actif", String(params.actif));

    const qs = searchParams.toString();
    const url = getApiUrl(`${ACCOUNTING_ENDPOINTS.LIST_ORG_OPERATION_TYPES}${qs ? `?${qs}` : ""}`);
    const response = await axiosInstance.get<{ operation_types: LedgerAllocationOperationType[] }>(url);
    // L'endpoint org-scoped sérialise des ids numériques : on normalise en
    // string comme partout ailleurs, sinon toute comparaison stricte casse.
    return (response.data.operation_types ?? []).map((item) => ({ ...item, id: String(item.id) }));
  },

  createOrgOperationType: async (payload: { name: string, budgetType: string, nature?: "expense" | "revenue" }): Promise<LedgerAllocationOperationType> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.CREATE_ORG_OPERATION_TYPE);
    const response = await axiosInstance.post<{ operation_type: LedgerAllocationOperationType }>(url, {
      name: payload.name,
      type_budget: payload.budgetType,
      nature: payload.nature,
    });
    return response.data.operation_type;
  },

  updateOrgOperationType: async (
    payload: {
      id: string
      nature?: "expense" | "revenue"
      name?: string
      actif?: boolean
      numeroCompte?: string | null
      defaultFor?: "refund" | null
    },
  ): Promise<LedgerAllocationOperationType> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.UPDATE_ORG_OPERATION_TYPE(payload.id));
    const response = await axiosInstance.patch<{ operation_type: LedgerAllocationOperationType }>(url, {
      ...(payload.nature !== undefined ? { nature: payload.nature } : {}),
      ...(payload.name !== undefined ? { name: payload.name } : {}),
      ...(payload.actif !== undefined ? { actif: payload.actif } : {}),
      // Vide = effacé : le compte comptable est facultatif.
      ...(payload.numeroCompte !== undefined ? { numero_compte: payload.numeroCompte ?? "" } : {}),
      ...(payload.defaultFor !== undefined ? { default_for: payload.defaultFor ?? "" } : {}),
    });
    return response.data.operation_type;
  },

  // ADM-621 : import du plan comptable général (référentiel figé côté API)
  // dans les catégories du type demandé, et son annulation.
  pcgImportOperationTypes: async (budgetType: string): Promise<{ createdCount: number, skippedCount: number }> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.PCG_IMPORT_OPERATION_TYPES);
    const response = await axiosInstance.post<{ created_count: number, skipped_count: number }>(url, { type_budget: budgetType });
    return { createdCount: response.data.created_count, skippedCount: response.data.skipped_count };
  },

  pcgRevertOperationTypes: async (budgetType: string): Promise<{ removedCount: number, keptCount: number, keptNames: string[] }> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.PCG_REVERT_OPERATION_TYPES);
    const response = await axiosInstance.post<{ removed_count: number, kept_count: number, kept_names: string[] }>(url, { type_budget: budgetType });
    return {
      removedCount: response.data.removed_count,
      keptCount: response.data.kept_count,
      keptNames: response.data.kept_names ?? [],
    };
  },

  mergeOrgOperationType: async (payload: { sourceId: string, targetId: string }): Promise<void> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.MERGE_ORG_OPERATION_TYPE(payload.sourceId));
    await axiosInstance.post(url, { target_operation_type_id: payload.targetId });
  },

  deleteOrgOperationType: async (operationTypeId: string): Promise<void> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.DELETE_ORG_OPERATION_TYPE(operationTypeId));
    await axiosInstance.delete(url);
  },

  async searchSuppliers(search: string): Promise<Array<{ id: string, name: string }>> {
    const url = getApiUrl("/suppliers");
    const params = buildSearchParams({ q: { name_cont: search.trim() || undefined }, limit: 30 });
    const response = await axiosInstance.get<{ suppliers?: Array<{ id: string | number, name: string }> }>(url, { params });
    return (response.data.suppliers ?? []).map((s) => ({ id: String(s.id), name: s.name }));
  },

  // ── Bank Accounts (Settings) ──────────────────────────────

  async listBankAccounts(): Promise<BankAccountSettings[]> {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.LIST_BANK_ACCOUNTS);
    const params = buildSearchParams({ limit: 1000 });
    const response = await axiosInstance.get<{
      bank_accounts: BankAccountSettings[]
      metadata?: { total_items: number, current_page: number, total_pages: number }
    }>(url, { params });
    return response.data.bank_accounts ?? [];
  },

  async createBankAccount(payload: BankAccountWritePayload): Promise<BankAccountSettings> {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.CREATE_BANK_ACCOUNT);
    const response = await axiosInstance.post<{ bank_account: BankAccountSettings }>(url, { bank_account: payload });
    return response.data.bank_account;
  },

  async updateBankAccount(id: number, payload: Partial<BankAccountWritePayload>): Promise<BankAccountSettings> {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.UPDATE_BANK_ACCOUNT(id));
    const response = await axiosInstance.patch<{ bank_account: BankAccountSettings }>(url, { bank_account: payload });
    return response.data.bank_account;
  },

  async deleteBankAccount(id: number): Promise<void> {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.DELETE_BANK_ACCOUNT(id));
    await axiosInstance.delete(url);
  },

  async designatePayboxCollection(id: number): Promise<void> {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.PAYBOX_COLLECTION(id));
    await axiosInstance.put(url);
  },

  async clearPayboxCollection(id: number): Promise<void> {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.PAYBOX_COLLECTION(id));
    await axiosInstance.delete(url);
  },

  // ── Bridge ──────────────────────────────────────────

  async fetchBridgeAccounts(): Promise<{ bridgeAccounts: BridgeAccount[], canDeleteWithPurge: boolean }> {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.BRIDGE_ACCOUNTS);
    const response = await axiosInstance.get<{ bridge_accounts: BridgeAccount[], can_delete_with_purge: boolean }>(url);
    return {
      bridgeAccounts: response.data.bridge_accounts ?? [],
      canDeleteWithPurge: response.data.can_delete_with_purge ?? false,
    };
  },

  async fetchBridgeProviders(
    params: { q?: string } = {},
  ): Promise<BridgeProvider[]> {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.BRIDGE_PROVIDERS);
    const response = await axiosInstance.get<{ bridge_providers: BridgeProvider[] }>(url, { params: { q: params.q || undefined } });
    return response.data.bridge_providers ?? [];
  },

  async bridgeConnect(): Promise<{ url: string }> {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.BRIDGE_CONNECT);
    const response = await axiosInstance.post<{ url: string }>(url);
    return response.data;
  },

  async bridgeMatch(bridgeAccountId: number, bankAccountId: number | null): Promise<void> {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.BRIDGE_MATCH);
    await axiosInstance.patch(url, {
      bridge_account_id: bridgeAccountId,
      bank_account_id: bankAccountId,
    });
  },

  async bridgeRefresh(bridgeAccountId: number): Promise<void> {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.BRIDGE_REFRESH);
    await axiosInstance.post(url, { bridge_account_id: bridgeAccountId });
  },

  async bridgeReconnect(bridgeAccountId: number): Promise<{ url: string }> {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.BRIDGE_RECONNECT);
    const response = await axiosInstance.post<{ url: string }>(url, { bridge_account_id: bridgeAccountId });
    return response.data;
  },

  async bridgeDesync(): Promise<void> {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.BRIDGE_DESYNC);
    await axiosInstance.post(url);
  },

  async bridgeDelete(): Promise<void> {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.BRIDGE_DELETE);
    await axiosInstance.post(url);
  },

  bridgeTransfer: async (bankMovementId: string): Promise<BridgeTransferResponse> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.BRIDGE_TRANSFER(bankMovementId));
    const response = await axiosInstance.post<BridgeTransferResponse>(url);
    return response.data;
  },

  bridgeConfirm: async (bankMovementId: string): Promise<void> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.BRIDGE_CONFIRM(bankMovementId));
    await axiosInstance.post(url);
  },

  // ADM-864 : état réel du payment request chez Bridge. Seule source fiable pour
  // savoir si le consentement a été donné, br_status n'arrivant que par webhook.
  bridgePaymentStatus: async (bankMovementId: string): Promise<BridgePaymentStatusResponse> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.BRIDGE_PAYMENT_STATUS(bankMovementId));
    const response = await axiosInstance.get<BridgePaymentStatusResponse>(url);
    return response.data;
  },

  /** `discardMovement` supprime aussi le mouvement créé pour le virement (chemin « solder par règlement »). */
  bridgeRelease: async (
    bankMovementId: string,
    discardMovement = false,
  ): Promise<BridgeReleaseResponse> => {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.BRIDGE_RELEASE(bankMovementId));
    const response = await axiosInstance.post<BridgeReleaseResponse>(
      url,
      discardMovement ? { discard_movement: true } : {},
    );
    return response.data;
  },

  // ── Entry Templates ──────────────────────────────────

  async fetchEntryTemplates() {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.LIST_ENTRY_TEMPLATES);
    const response = await axiosInstance.get<{ accounting_entry_templates: import("../types/accounting.types").AccountingEntryTemplate[] }>(url);
    return response.data;
  },

  async createEntryTemplate(payload: import("../types/accounting.types").AccountingEntryTemplateCreatePayload) {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.CREATE_ENTRY_TEMPLATE);
    const response = await axiosInstance.post<{ accounting_entry_template: import("../types/accounting.types").AccountingEntryTemplate }>(url, { accounting_entry_template: payload });
    return response.data;
  },

  async deleteEntryTemplate(id: string) {
    const url = getApiUrl(ACCOUNTING_ENDPOINTS.DELETE_ENTRY_TEMPLATE(id));
    await axiosInstance.delete(url);
  },
};
